import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useFeatureFlag } from '@fs/zion-flags'
import { SPECIAL_COLLECTIONS } from '../collectionConstants'

/**
 * Custom hook to manage the state and properties of an alert banner for special collections.
 *
 * @param {Object} params - The parameters object.
 * @param {string} params.collectionId - The ID of the collection to check for special configurations.
 * @returns {Object} An object containing the properties for the alert banner and a flag indicating whether to show the banner.
 * @returns {Object} return.alertBannerProps - The properties for the alert banner component.
 * @returns {string} return.alertBannerProps.message - The message to display in the alert banner.
 * @returns {string} return.alertBannerProps.confirmText - The text for the confirm button.
 * @returns {string} return.alertBannerProps.dismissText - The text for the dismiss button.
 * @returns {function} return.alertBannerProps.onConfirm - The function to call when the confirm button is clicked.
 * @returns {function} return.alertBannerProps.onDismiss - The function to call when the dismiss button is clicked.
 * @returns {boolean} return.alertBannerProps.open - The state indicating whether the banner is open.
 * @returns {function} return.alertBannerProps.setOpen - The function to set the open state of the banner.
 * @returns {boolean} return.showBanner - A flag indicating whether to show the banner.
 */
export default function useCollectionAlertBanner({ collectionId }) {
  const [open, setOpen] = useState(true)
  const [t] = useTranslation()

  const collectionConfig = SPECIAL_COLLECTIONS[collectionId]
  const featureFlag = useFeatureFlag(collectionConfig?.flag)

  const basicAlertMessage = t(
    'index.alert.banner-message',
    'Due to some issues, this record collection is unavailable for edits.'
  )

  const getAlertMessage = () => {
    if (featureFlag?.isOn) {
      if (collectionConfig?.alertMessageKey && collectionConfig?.alertMessageValue)
        return t(collectionConfig.alertMessageKey, collectionConfig.alertMessageValue)
      return basicAlertMessage
    }
    return t('index.alert.banner-message', 'Due to some issues, this record collection is unavailable for edits.')
  }

  // After investigating RIP-2416, we met with the zion team and decided that the 1931 census banner should be shown in context of the data being edited, not as an AlertBanner for the page.
  // It has been converted, but the same probably goes for other banners using this pattern, as well.
  const alertBannerProps = {
    message: getAlertMessage(),
    confirmText: t('index.1931.Census.alert.confirm-button', 'Confirm'),
    dismissText: t('index.1931.Census.alert.dismiss-button', 'Dismiss'),
    onConfirm: (closeBanner) => closeBanner(),
    onDismiss: (closeBanner) => closeBanner(),
    open,
    setOpen,
  }

  const showBanner = featureFlag?.isOn

  return { alertBannerProps, showBanner }
}
